/**
 * 成就商店面板
 * 设计原型已归档（2026-10-24）：原基准 docs/页面原型.md §页面8 + ui-concept/成就商店.html；现行实现见本文件 + docs/UI规范.md §2.1/§3
 * 仅主页可进入（主页按钮完成一周目结局后开启）；成就列表 + 跨周目商店双 Tab。
 * 用户定制：成就薄荷块不显示类别标题（去掉咖位类/里程碑类等 label）。
 * 成就分组按 AchievementDef.category 动态生成（2026-09-29 新表起，⛔ 再回硬编码 ID 分组）。
 */
import type { GameApp } from '../../app';
import { saveSystem } from '../../systems/SaveSystem';
import { shopSystem } from '../../systems/ShopSystem';
import { rankSystem } from '../../systems/RankSystem';
import { GameData } from '../../data/GameData';
import { AttributeKey, AchievementCategory, ShopPurchases } from '../../data/types';
import { attrName, createDiv, createSpan, createButton, createBtn, createHr, appendChildren, gemText, createInfoTip } from '../ui';
import { setNum } from '../feedback';
import { icon } from '../icons';

/** 面板分组顺序（按 category 分薄荷块，块内按表序；隐藏块压尾） */
const CATEGORY_ORDER: AchievementCategory[] = [
    'milestone', 'rank', 'attribute', 'money', 'works', 'score',
    'awards', 'fans', 'social', 'training', 'endings', 'hidden',
];

/** 商店分组标题（价格动态取自 ShopSystem，防文案与实价脱节） */
function shopGroupTitle(key: keyof ShopPurchases): string {
    if (key === 'rank') return `开局咖位 · 阶梯价 💎${shopSystem.RANK_STEPS.join('/')}`;
    if (key === 'attrs') return `开局属性加成 · 💎${shopSystem.PRICE.attrs}/级 · 每项最多 10 级`;
    return `额外特性点 · 💎${shopSystem.PRICE.points}/点 · 最多 ${shopSystem.MAX.points} 点`;
}

export class AchievementShopPanel {
    readonly element: HTMLElement;

    private pointsVal: HTMLElement;
    private resetBtn: HTMLButtonElement;
    private tabAch: HTMLElement;
    private tabShop: HTMLElement;
    private secAch: HTMLElement;
    private secShop: HTMLElement;
    private achBlocks: HTMLElement;   // 成就列表容器
    private storeGroups: HTMLElement; // 商店容器

    /** 当前激活 Tab：'ach' | 'shop' */
    private activeTab: 'ach' | 'shop' = 'ach';

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root');
        const inner = createDiv('shop-inner');

        // ===== 头部：标题居中（2026-09-29 用户定：⛔ 左上角返回按钮，返回只留底部一颗） =====
        const head = createDiv('shop-head');
        head.appendChild(createSpan('head-title', '成就商店'));
        inner.appendChild(head);

        // ===== 成就点行 =====
        const points = createDiv('points-row');
        const pointsLabel = createSpan('points-label');
        appendChildren(pointsLabel, gemText('💎 成就点'));
        points.appendChild(pointsLabel);
        this.pointsVal = createSpan('points-val', '0');
        points.appendChild(this.pointsVal);
        this.resetBtn = createButton('reset-btn', '重置成就点', () => this.onReset());
        points.appendChild(this.resetBtn);
        inner.appendChild(points);

        inner.appendChild(createHr());

        // ===== 类型页签 =====
        const tabs = createDiv('type-tabs');
        this.tabAch = createBtn('type-tab active', [icon('trophy'), '成就']);
        this.tabShop = createBtn('type-tab', [icon('cart'), '商店']);
        tabs.appendChild(this.tabAch);
        tabs.appendChild(this.tabShop);
        inner.appendChild(tabs);
        this.tabAch.addEventListener('click', () => this.switchTab('ach'));
        this.tabShop.addEventListener('click', () => this.switchTab('shop'));

        // ===== 成就列表容器 =====
        this.secAch = createDiv('shop-sec');
        this.achBlocks = createDiv('ach-groups');
        this.secAch.appendChild(this.achBlocks);
        inner.appendChild(this.secAch);

        // ===== 商店容器 =====
        this.secShop = createDiv('shop-sec hidden-panel');
        this.storeGroups = createDiv('store-groups');
        this.secShop.appendChild(this.storeGroups);
        inner.appendChild(this.secShop);

        // ===== 底部返回 =====
        const backHome = createButton('btn btn-primary shop-back', '返回主页', () => this.app.show('mainMenu'), 'home');
        inner.appendChild(backHome);

        panel.appendChild(inner);
        this.element = panel;
    }

    /** 切换成就/商店 Tab */
    private switchTab(tab: 'ach' | 'shop'): void {
        this.activeTab = tab;
        this.tabAch.classList.toggle('active', tab === 'ach');
        this.tabShop.classList.toggle('active', tab === 'shop');
        this.secAch.classList.toggle('hidden-panel', tab !== 'ach');
        this.secShop.classList.toggle('hidden-panel', tab !== 'shop');
    }

    // ===== 商店操作 =====

    /** 购买一级 */
    private onBuy(kind: keyof ShopPurchases, attr?: AttributeKey): void {
        // 点数不足/已达上限时 `+` 钮本就 disabled（storeRow 的 plus.disabled），这里只做系统层兜底，
        // ⛔ 不再弹「成就点不足或已达上限」——两种原因混成一句、且玩家点不到这颗钮
        if (shopSystem.buy(kind, attr)) this.refresh();
    }

    /** 降级一级（退回成就点） */
    private onRefund(kind: keyof ShopPurchases, attr?: AttributeKey): void {
        if (shopSystem.refund(kind, attr)) {
            this.refresh();
        }
    }

    /** 重置：全部购买项归零，退回所有已消费成就点（无消费时钮本就 disabled，此处只做兜底、⛔ 不弹提示） */
    private onReset(): void {
        if (shopSystem.totalSpent() <= 0) return;
        shopSystem.reset();
        this.refresh();
    }

    // ===== 渲染 =====

    refresh(): void {
        const meta = saveSystem.loadMeta();
        setNum(this.pointsVal, meta.achievementPoints);
        this.resetBtn.disabled = shopSystem.totalSpent() <= 0;

        this.renderAchievements();
        this.renderStore();
    }

    /** 成就列表：按 category 分薄荷块，无标题 */
    private renderAchievements(): void {
        this.achBlocks.innerHTML = '';
        const unlocked = new Set(saveSystem.loadMeta().unlockedAchievements);

        for (const cat of CATEGORY_ORDER) {
            const defs = GameData.ACHIEVEMENTS.filter(d => (d.category ?? 'milestone') === cat);
            if (defs.length === 0) continue;
            const block = createDiv('ach-block');   // 薄荷块（无标题）
            defs.forEach(def => {
                const isUnlocked = unlocked.has(def.id);
                const item = createDiv(isUnlocked ? 'ach-item' : 'ach-item locked');

                // 状态：已解锁 ✓ 图标；未解锁 🔒 图标（含隐藏成就「？？？」）
                const status = createSpan(isUnlocked ? 'ach-status' : 'ach-status locked');
                status.appendChild(icon(isUnlocked ? 'check' : 'lock'));
                item.appendChild(status);

                const main = createDiv('ach-main');
                if (isUnlocked || !def.isHidden) {
                    main.appendChild(createSpan(isUnlocked ? 'ach-name' : 'ach-name locked', def.name));
                    main.appendChild(createSpan(isUnlocked ? 'ach-desc' : 'ach-desc locked', def.description));
                } else {
                    main.appendChild(createSpan('ach-name locked', '？？？'));
                    main.appendChild(createSpan('ach-desc locked', '隐藏成就 · 达成后揭晓'));
                }
                item.appendChild(main);

                const pts = createSpan(isUnlocked ? 'ach-points' : 'ach-points locked', `+${def.points}`);
                item.appendChild(pts);

                block.appendChild(item);
            });
            this.achBlocks.appendChild(block);
        }
    }

    /** 商店：三个分组，stepper 即时结算 */
    private renderStore(): void {
        this.storeGroups.innerHTML = '';
        const p = shopSystem.getPurchases();
        const meta = saveSystem.loadMeta();
        const points = meta.achievementPoints;

        // 开局咖位（阶梯价：按钮可用性按「买下一级的花费」判定）
        const rankGroup = this.shopGroup('rank');
        rankGroup.appendChild(this.storeRow(
            '咖位',
            // 咖位口径（2026-10-23 UI规范 §6）：玩家可见读数一律用咖位名，`Lv.` 只留控制台/测试
            rankSystem.getRankName(1 + p.rank),
            p.rank,
            shopSystem.MAX.rank,
            () => this.onBuy('rank'),
            () => this.onRefund('rank'),
            points,
            shopSystem.priceOf('rank', p.rank),
        ));
        this.storeGroups.appendChild(rankGroup);

        // 开局属性加成（七维）
        const attrsGroup = this.shopGroup('attrs');
        GameData.ATTRIBUTE_KEYS.forEach((key) => {
            const lv = p.attrs[key] ?? 0;
            attrsGroup.appendChild(this.storeRow(
                attrName(key),
                `+${lv * shopSystem.ATTR_BONUS_PER_LEVEL}`,
                lv,
                shopSystem.MAX.attrs,
                () => this.onBuy('attrs', key),
                () => this.onRefund('attrs', key),
                points,
                shopSystem.priceOf('attrs', lv),
            ));
        });
        this.storeGroups.appendChild(attrsGroup);

        // 额外特性点（2026-10-21 点数制：原「额外天赋槽」）
        const pointsGroup = this.shopGroup('points');
        pointsGroup.appendChild(this.storeRow(
            '额外特性点',
            `×${p.points}`,
            p.points,
            shopSystem.MAX.points,
            () => this.onBuy('points'),
            () => this.onRefund('points'),
            points,
            shopSystem.priceOf('points', p.points),
        ));
        this.storeGroups.appendChild(pointsGroup);
    }

    /** 商店分组薄荷块（带标题行；布局表达不了的机制说明走 info+tooltip，不挂常驻脚注） */
    private shopGroup(key: keyof ShopPurchases): HTMLElement {
        const group = createDiv('group-block');
        const titleEl = createSpan('group-title');
        appendChildren(titleEl, gemText(shopGroupTitle(key)));
        if (key === 'attrs') {
            titleEl.appendChild(createInfoTip('叠加在开局随机属性之上，买几级加几级'));
        }
        group.appendChild(titleEl);
        return group;
    }

    /** 单条商店行：名称 + stepper（−/值/+）；price = 买下一级的花费 */
    private storeRow(
        name: string,
        valLabel: string,
        level: number,
        max: number,
        onPlus: () => void,
        onMinus: () => void,
        points: number,
        price: number,
    ): HTMLElement {
        const row = createDiv('store-row');
        row.appendChild(createSpan('sr-name', name));
        const stepper = createDiv('stepper');
        const minus = createButton('st-btn', '−', onMinus);
        minus.disabled = level <= 0;
        const val = createSpan('st-val', valLabel);
        const plus = createButton('st-btn', '+', onPlus);
        plus.disabled = level >= max || points < price;
        stepper.appendChild(minus);
        stepper.appendChild(val);
        stepper.appendChild(plus);
        row.appendChild(stepper);
        return row;
    }

    destroy(): void {
        // 无外部事件监听
    }
}
